import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { Formik, Field } from 'formik';
import moment from 'moment';
import { toast } from 'react-toastify';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import Layout from '../../DashboardLayout';
import Report from '../../../components/features/overtime/Report';
import { teamService, reportService, clientsService } from '../../../services';
import Loading from '../../../components/Loader';
import {
  MemberMultiSelectDropdown,
  MultiSelectItemDropdown,
} from '../../../components/common';
import { OT_DATE_RANGE, PAGINATION_LIMIT } from '../../../constants';

class OvertimeReport extends Component {
  state = {
    isLoading: true,
    siteId: [],
    clientId: [],
    teamMembersList: [],
    teamGroupList: [],
    startDate: moment().startOf('week'),
    endDate: moment().endOf('week'),
    overtime: [],
    overtimeMetadata: {},
    isLoadingReport: true,
    isFetchingOvertimeData: false,
    numberOfDaysSelected: 1,
    daysLimit: 7,
    selectedTeamMember: 'all',
    reportFormData: {},
    page: 1,
    limit: PAGINATION_LIMIT,
    clientsData: [],
    changeSource: null,
  };

  pageHeading = {
    preTitle: 'Reports',
    title: 'Overtime Report',
  };

  overTimeSelectInputRef = React.createRef();

  selectInputRef = React.createRef(null);

  clientSelectInputRef = React.createRef();

  siteSelectInputRef = React.createRef();

  componentDidMount() {
    this.getClientData();
    this.getTeamsData();
    this.getTeamGroupList();
  }

  componentDidUpdate(prevProps, prevState) {
    const { changeSource } = this.state;

    // Only act if changeSource has changed
    if (!changeSource || changeSource === prevState.changeSource) return;

    const formik = this.overTimeSelectInputRef.current;
    const memberSelect = this.selectInputRef?.current?.select;
    const clientSelect = this.clientSelectInputRef?.current?.select;
    const projectSelect = this.projectSelectInputRef?.current?.select;

    const clearSelect = (selectRef) => selectRef?.clearValue();
    const resetFields = (fields) =>
      fields.forEach((f) => formik.setFieldValue(f, []));

    switch (changeSource) {
      case 'site':
        formik.setFieldValue('team', 'all');
        formik.setFieldValue('task', 'all');
        resetFields(['clientId', 'project', 'teamMember']);
        [memberSelect, clientSelect, projectSelect].forEach(clearSelect);

        this.getClientData();
        setTimeout(() => {
          this.getTeamsData();
        }, 200);
        break;

      case 'client':
        formik.setFieldValue('team', 'all');
        formik.setFieldValue('task', 'all');
        resetFields(['teamMember', 'project']);
        [memberSelect, projectSelect].forEach(clearSelect);
        this.getTeamsData();
        break;

      default:
        break;
    }

    // Reset changeSource after handling to prevent re-trigger
    this.setState({ changeSource: null });
  }

  setCurrentPage = (pageN) => {
    this.setState((state) => ({
      ...state,
      page: pageN,
    }));
  };

  getClientData = () => {
    const { siteId } = this.state;
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));
    const clientParams = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      status: 'active',
      siteId,
    };
    if (siteId.length === 0 || siteId.includes('all')) {
      delete clientParams.siteId;
    }
    clientsService
      .get_client_list(clientParams)
      .then((clientData) => {
        if (clientData.data) {
          this.setState((state) => ({
            ...state,
            isLoading: false,
            clientsData: [...clientData.data],
          }));
        }
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  handleGenerateData = (formdata) => {
    this.setState((state) => ({
      ...state,
      isFetchingOvertimeData: true,
    }));

    const { startDate, endDate, page } = this.state;
    const dataFromDate = moment(startDate).format('YYYY-MM-DD');
    const dataToDate = moment(endDate).format('YYYY-MM-DD');
    const params = {
      teamOwnerId: formdata.teamOwnerId,
      siteId: formdata.siteId,
      clientId: formdata.clientId,
      team: [formdata.team],
      teamMemberId: formdata.teamMember,
      fromDate: dataFromDate.concat(' 00:00:00'),
      toDate: dataToDate.concat(' 23:59:59'),
      page: formdata.page || page,
      limit: formdata.limit || PAGINATION_LIMIT,
      excludeArchivedProjects: formdata.excludeArchivedProjects,
    };
    if (
      (formdata.siteId && formdata.siteId.length === 0) ||
      (formdata.siteId && formdata.siteId.includes('all'))
    ) {
      delete params.siteId;
    }
    if (
      (formdata.clientId && formdata.clientId.length === 0) ||
      (formdata.clientId && formdata.clientId.includes('all'))
    ) {
      delete params.clientId;
    }
    if (formdata.team === 'all' || formdata.team === '') {
      delete params.team;
    }
    if (
      formdata.teamMember === 'all' ||
      formdata.teamMember === '' ||
      formdata.teamMember.length === 0 ||
      formdata.teamMember.includes('all')
    ) {
      delete params.teamMemberId;
    }
    reportService
      .get_overtime_data(params)
      .then((response) => {
        const {
          data: { metadata, overtimeDetails },
        } = response;

        this.setState((state) => ({
          ...state,
          isFetchingOvertimeData: false,
          overtime: overtimeDetails,
          overtimeMetadata: metadata,
          isLoadingReport: false,
          reportFormData: formdata,
        }));
      })
      .catch((error) => {
        this.setState((state) => ({
          ...state,
          isFetchingOvertimeData: false,
          isLoadingReport: false,
        }));
      });
  };

  handleDateSelect = (event, picker) => {
    picker.element.val(
      `${moment(picker.startDate).day(0).format('MMM D, YYYY')} - ${moment(
        picker.endDate
      )
        .day(6)
        .format('MMM D, YYYY')}`
    );
    this.setState((state) => ({
      ...state,
      startDate: moment(picker.startDate).day(0),
      endDate: moment(picker.endDate).day(6),
    }));
  };

  handleDateRangeValidation = (start, end) => {
    const { daysLimit } = this.state;
    const daysSelected = Math.abs(moment(start).diff(moment(end), 'd'));
    if (daysSelected > daysLimit) {
      toast.error(`${OT_DATE_RANGE.LIMIT} ${OT_DATE_RANGE.ERROR}`);
    }
    this.setState((state) => ({
      ...state,
      numberOfDaysSelected: daysSelected,
    }));
  };

  getTeamsData = () => {
    const { siteId, clientId } = this.state;
    this.setState((state) => ({
      ...state,
      isLoading: true,
    }));

    const teamParams = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      summaryList: true,
      siteId,
      clientId,
    };

    if (siteId.length === 0 || siteId.includes('all')) {
      delete teamParams.siteId;
    }
    if (clientId.length === 0 || clientId.includes('all')) {
      delete teamParams.clientId;
    }

    teamService
      .get_team_members_data(teamParams)
      .then((response) => {
        const { members } = response.data;
        this.setState((state) => ({
          ...state,
          teamMembersList: [...members],
          isLoading: false,
        }));
      })
      .catch(() => {
        this.setState((state) => ({
          ...state,
          isLoading: false,
        }));
      });
  };

  getTeamGroupList = () => {
    teamService
      .get_team_group_list({
        teamOwnerId: localStorage.getItem('teamOwnerId'),
      })
      .then((response) => {
        if (response.data) {
          this.setState(() => ({
            teamGroupList: response.data,
          }));
        }
      });
  };

  handleSiteChange = (selectedSites) => {
    this.setState({
      siteId: selectedSites,
      isUpdatingProjectsList: true,
      changeSource: 'site',
    });
  };

  handleClientChange = (selelctedClient) => {
    this.setState({
      clientId: selelctedClient,
      changeSource: 'client',
    });
  };

  render() {
    const {
      teamMembersList,
      startDate,
      endDate,
      overtime,
      overtimeMetadata,
      isLoadingReport,
      isFetchingOvertimeData,
      isLoading,
      numberOfDaysSelected,
      daysLimit,
      reportFormData,
      page,
      teamGroupList,
      clientsData,
    } = this.state;
    // Team owner ID
    const teamOwnerId = localStorage.getItem('teamOwnerId');
    const { hierarchy, permissions } = this.props;
    return (
      <Layout pageHeading={this.pageHeading}>
        <Formik
          initialValues={{
            teamOwnerId,
            siteId: [],
            clientId: [],
            team: 'all',
            teamMember: [],
            fromDate: startDate,
            toDate: endDate,
            page: 1,
            limit: PAGINATION_LIMIT,
            excludeArchivedProjects: true,
          }}
          onSubmit={(values) => {
            this.handleGenerateData(values);
          }}
          innerRef={this.overTimeSelectInputRef}
        >
          {(formikprops) => (
            <Form onSubmit={formikprops.handleSubmit} className="mb-5">
              <Row>
                {hierarchy?.sites?.length > 0 && (
                  <Col md={6} lg={3}>
                    <MultiSelectItemDropdown
                      id="siteSelectInputRef"
                      name="siteId"
                      labelText="Sites"
                      fromArray={hierarchy.sites.map((site) => ({
                        value: site._id,
                        label: site.siteName,
                      }))}
                      formProps={formikprops}
                      isDisabled={hierarchy.sites.length < 1 && true}
                      innerRef={this.siteSelectInputRef}
                      isAllItemOption
                      isAllItemValuesWithNoArray
                      showErrorMessage
                      onChangeAction={this.handleSiteChange}
                    />
                  </Col>
                )}
                {permissions &&
                  permissions.includes('client_manage') &&
                  clientsData?.length > 0 && (
                    <Col md={6} lg={3}>
                      <MultiSelectItemDropdown
                        id="clientSelectInputRef"
                        name="clientId"
                        labelText="Clients"
                        fromArray={clientsData.map((client) => ({
                          value: client._id,
                          label: client.name,
                        }))}
                        formProps={formikprops}
                        isDisabled={clientsData.length < 1 && true}
                        innerRef={this.clientSelectInputRef}
                        isAllItemOption
                        isAllItemValuesWithNoArray
                        showErrorMessage
                        onChangeAction={this.handleClientChange}
                      />
                    </Col>
                  )}
                {permissions?.includes('team_member_view') &&
                  permissions.includes('others_report_view') && (
                    <Col md={6} lg={3}>
                      <MemberMultiSelectDropdown
                        id="selectInputRef"
                        name="teamMember"
                        labelText="Team Member(s)"
                        fromArray={teamMembersList}
                        formProps={formikprops}
                        isDisabled={teamMembersList.length < 1 && true}
                        innerRef={this.selectInputRef}
                        isAllTeamMemberOption
                        isAllTeamMemberValuesWithNoArray
                        requiredValueAs="userId"
                        showErrorMessage
                      />
                    </Col>
                  )}
                <Col md={6} lg={3}>
                  <Form.Group controlId="team">
                    <Form.Label>Team Group</Form.Label>
                    <Field className="form-select" as="select" name="team">
                      <option value="all">All</option>
                      {teamGroupList &&
                        teamGroupList.length > 0 &&
                        teamGroupList.map((teamGroup, index) => (
                          <option
                            key={`teamGroup${index}`}
                            value={teamGroup._id}
                          >
                            {teamGroup.name}
                          </option>
                        ))}
                    </Field>
                  </Form.Group>
                </Col>
                <Col md={6} lg={3}>
                  <Form.Group>
                    <Form.Label>Date range</Form.Label>
                    <DateRangePicker
                      onEvent={this.handleDateSelect}
                      initialSettings={{
                        autoUpdateInput: false,
                        singleDatePicker: true,
                        startDate: moment().toDate(),
                        locale: {
                          format: 'MMM D, YYYY',
                        },
                        alwaysShowCalendars: true,
                        autoApply: true,
                      }}
                    >
                      <input
                        type="text"
                        className="form-control icon-calendar"
                        placeholder={`${moment()
                          .day(0)
                          .format('MMM D, YYYY')} - ${moment()
                          .day(6)
                          .format('MMM D, YYYY')}`}
                      />
                    </DateRangePicker>
                  </Form.Group>
                </Col>
                <Col xs={12}>
                  <Form.Group controlId="excludeArchivedProjects">
                    <Form.Check
                      name="excludeArchivedProjects"
                      type="checkbox"
                      label="Exclude archived projects"
                      checked={formikprops.values.excludeArchivedProjects}
                      className="pt-3"
                      onChange={() =>
                        formikprops.setFieldValue(
                          'excludeArchivedProjects',
                          !formikprops.values.excludeArchivedProjects
                        )
                      }
                    />
                  </Form.Group>
                  <Form.Group controlId="generateData">
                    <div>
                      <Button
                        variant="primary"
                        type="Submit"
                        disabled={numberOfDaysSelected > daysLimit}
                      >
                        Generate
                      </Button>
                    </div>
                  </Form.Group>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
        {!isLoadingReport && (
          <Report
            overTimeMetadata={overtimeMetadata}
            overtimeData={overtime}
            startDate={startDate}
            endDate={endDate}
            setCurrentPage={this.setCurrentPage}
            getOvertimeData={this.handleGenerateData}
            reportFormData={reportFormData}
            pageNumber={page}
          />
        )}
        {/* Loader */}
        {(isLoading || isFetchingOvertimeData) && <Loading contentAreaOnly />}
      </Layout>
    );
  }
}

const mapStateToProps = (state) => {
  const { hierarchy, permissionsReducer } = state;
  return {
    hierarchy,
    permissions: permissionsReducer,
  };
};

OvertimeReport.propTypes = {
  hierarchy: PropTypes.object,
  permissions: PropTypes.array,
};

export default connect(mapStateToProps)(OvertimeReport);
